'use client';
import { useState } from 'react';
import Image from 'next/image';
import { motion } from 'motion/react';
import { type LinkField } from 'lib/types';
import { cn } from 'lib/utils';
import { Button, type ButtonProps, RichText, Wrapper } from 'ui';

export type HeroProps = {
  eyebrow?: string;
  title: string;
  content?: string;
  background?: string;
  link1?: LinkField;
  link1Variant?: ButtonProps['variant'];
  link2?: LinkField;
  link2Variant?: ButtonProps['variant'];
  showHsaFsaBadge?: boolean;
  disableMotion?: boolean;
};

const CONTACT_US_TRACKING_PROPS = {
  event: 'CTA_clicked',
  properties: {
    cta: 'Contact Us',
    location: 'hero banner',
    path: '/contact',
    action: 'go to contact us',
  },
};

const Hero = ({
  eyebrow,
  title,
  content,
  link1,
  link1Variant = 'primary',
  link2,
  link2Variant = 'tertiary',
  background,
  showHsaFsaBadge = false,
  disableMotion = false,
}: HeroProps) => {
  const [bgIsLoaded, setBgIsLoaded] = useState(false);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 1 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30,
      filter: 'blur(4px)',
    },
    visible: {
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      transition: {
        duration: 0.6,
        ease: 'easeOut' as const,
      },
    },
  };

  return (
    <section className="relative z-2 lg:bg-graphite-900 lg:px-5 lg:pb-5 min-h-[calc(100vh-var(--spacing-header-utility-height))] flex flex-col">
      <Wrapper
        variant="header"
        className="relative grow flex flex-col justify-end pt-header-height-mobile lg:pt-header-height-full pb-wrapper-header-x"
      >
        {/* Content Area */}
        <motion.div
          className="grow relative z-4 flex flex-col lg:max-w-180"
          variants={disableMotion ? undefined : containerVariants}
          initial={disableMotion ? undefined : 'hidden'}
          animate={
            disableMotion ? undefined : bgIsLoaded ? 'visible' : 'hidden'
          }
        >
          <header className="grow flex flex-col justify-center gap-6 max-lg:text-center py-8">
            {!!eyebrow && (
              <motion.h1
                className="text-eyebrow font-mono uppercase"
                variants={disableMotion ? undefined : itemVariants}
              >
                {eyebrow}
              </motion.h1>
            )}
            <motion.h2
              className="text-h1"
              variants={disableMotion ? undefined : itemVariants}
            >
              {title}
            </motion.h2>
          </header>

          {!!content && (
            <motion.div variants={disableMotion ? undefined : itemVariants}>
              <RichText
                className="max-lg:text-center max-w-120 max-lg:mx-auto text-p1"
                {...{ content }}
              />
            </motion.div>
          )}

          {(!!link1 || !!link2) && (
            <motion.div
              className="mt-8 flex flex-col items-center lg:flex-row lg:flex-wrap gap-4"
              variants={disableMotion ? undefined : itemVariants}
            >
              {link1 && (
                <Button
                  href={link1.href}
                  target={link1.target}
                  variant={link1Variant}
                  trackingProps={CONTACT_US_TRACKING_PROPS}
                >
                  {link1.text}
                </Button>
              )}

              {link2 && (
                <Button
                  href={link2.href}
                  target={link2.target}
                  variant={link2Variant}
                >
                  {link2.text}
                </Button>
              )}
            </motion.div>
          )}

          {!!showHsaFsaBadge && (
            <motion.p
              className="mt-8 flex items-center justify-center lg:justify-start gap-1"
              variants={disableMotion ? undefined : itemVariants}
            >
              <span className="sr-only">FSA or HSA eligible</span>
              <Image
                src="/_assets/hsa-fsa-badge.svg"
                alt="HSA/FSA Eligible"
                width={148}
                height={20}
                aria-hidden="true"
              />
            </motion.p>
          )}
        </motion.div>

        {!!background && (
          <>
            {/* Color Gradient */}
            <div
              className="z-2 absolute bottom-0 left-0 h-full w-full lg:w-240 max-lg:bg-graphite-950/50 lg:bg-gradient-to-r lg:from-graphite-950/77 lg:to-transparent rounded-bl-lg"
              aria-hidden="true"
            />

            {/* Blur Gradient */}
            <div
              className="max-lg:hidden z-3 absolute bottom-0 left-0 h-full w-240 mask-[linear-gradient(90deg,black,black,transparent)] backdrop-blur rounded-bl-lg"
              aria-hidden="true"
            />

            {/* Background Image */}
            <Image
              src={background}
              alt={title}
              fill={true}
              className={cn(
                'absolute size-full object-cover pointer-events-none rounded-b-lg transition-opacity duration-500 ease-in-out opacity-0',
                {
                  'opacity-100': bgIsLoaded,
                }
              )}
              priority
              onLoad={() => setBgIsLoaded(true)}
            />
          </>
        )}
      </Wrapper>
    </section>
  );
};

export default Hero;
